<script setup lang="ts">
import { useId } from "vue"

// The subscription gem (badge next to a subscriber's name, the subscription page's mocks). Fixed
// brand colours, so it reads the same in both themes; size it with `class` (it fills its box).
// The gradient id is per instance: duplicate ids break the fill wherever an earlier copy with
// that id is display:none.
//
// `outline`: a line icon in `currentColor` that sits with the other nav icons (sidebar); it fills
// with the brand colours when its menu button is hovered, or when `active`.

withDefaults(defineProps<{ outline?: boolean; active?: boolean }>(), { outline: false, active: false })

const gradientId = `subscription-gem-${useId()}`

const OUTER = "M70.5 46.0 L67.8 46.3 L65.2 47.0 L62.8 48.3 L60.7 49.9 L37.7 76.9 L35.5 80.5 L34.7 83.2 L34.4 86.0 L35.0 90.2 L36.1 92.8 L37.7 95.1 L90.3 156.5 L93.5 158.9 L97.3 160.2 L101.3 160.4 L104.0 159.9 L107.6 158.2 L110.6 155.5 L162.3 95.1 L164.5 91.5 L165.3 88.8 L165.6 86.0 L165.0 81.8 L163.9 79.2 L162.3 76.9 L140.2 50.9 L137.2 48.3 L134.8 47.0 L132.2 46.3 L129.5 46.0Z"
const INNER = "M82.1 66.0 L79.1 66.6 L77.2 67.7 L76.1 68.7 L65.7 82.9 L65.0 84.2 L64.3 86.4 L64.2 87.9 L64.6 90.2 L66.1 92.8 L94.5 125.6 L96.5 126.9 L98.8 127.7 L100.4 127.8 L102.8 127.3 L104.9 126.1 L106.1 125.0 L134.3 92.2 L135.1 90.9 L135.7 88.7 L135.8 87.1 L135.3 84.9 L134.3 82.9 L124.4 69.3 L122.8 67.7 L120.9 66.6 L117.9 66.0Z"
</script>

<template>
  <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="30 40 140 126"
      aria-hidden="true"
      focusable="false"
      :class="outline && ['gem-outline', active && 'gem-active']"
  >
    <defs>
      <linearGradient :id="gradientId" x1="100" y1="24" x2="100" y2="176" gradientUnits="userSpaceOnUse">
        <stop offset="0" stop-color="#B97CFF" />
        <stop offset="1" stop-color="#7A3EE0" />
      </linearGradient>
    </defs>
    <g class="gem-fill">
      <path :d="OUTER" :fill="`url(#${gradientId})`" />
      <path :d="INNER" fill="#E6D0FF" />
    </g>
    <!-- Line weight of a Lucide icon at the sidebar's 16px (stroke 2 on a 24 grid = 1.33px). -->
    <g v-if="outline" class="gem-lines" fill="none" stroke="currentColor" stroke-linejoin="round">
      <path :d="OUTER" vector-effect="non-scaling-stroke" stroke-width="1.33" />
      <path :d="INNER" vector-effect="non-scaling-stroke" stroke-width="1" />
    </g>
  </svg>
</template>

<style scoped>
.gem-outline .gem-fill,
.gem-outline .gem-lines {
  transition: opacity 200ms ease;
}
.gem-outline .gem-fill {
  opacity: 0;
}
/* Fills when its menu button (or whatever button/link holds it) is hovered or focused, or when active. */
:is(button, a):is(:hover, :focus-visible) > .gem-outline .gem-fill,
.gem-outline.gem-active .gem-fill {
  opacity: 1;
}
:is(button, a):is(:hover, :focus-visible) > .gem-outline .gem-lines,
.gem-outline.gem-active .gem-lines {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .gem-outline .gem-fill,
  .gem-outline .gem-lines {
    transition: none;
  }
}
</style>
